import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { GoogleLogin } from '@react-oauth/google'
import { AlertCircle, ArrowRight, Eye, EyeOff } from 'lucide-react'
import axios from 'axios'
import SEO from '../components/SEO'
import { login } from '../actions/userActions'
import {
  USER_LOGIN_REQUEST,
  USER_LOGIN_SUCCESS,
  USER_LOGIN_FAIL,
  USER_LOGOUT,
} from '../constants/userConstants'
import './LoginScreen.css'

// The Google control is a cross-origin iframe: it takes a pixel width and
// cannot be styled or made fluid. These two values match the form column at
// each breakpoint so its edge lines up with the fields instead of floating.
const G_WIDTH_WIDE = '384' // === .ls__col max-width (24rem)
const G_WIDTH_NARROW = '280' // fits a 320px viewport with the page gutters

// The product's own published data-handling claims, carried over from the
// landing page. They belong at the moment credentials are handed over.
const ASSURANCES = [
  {
    term: 'Nothing is stored',
    desc: 'No report content is retained on our systems after the request completes.',
  },
  {
    term: 'Encrypted in transit',
    desc: 'All traffic is encrypted in line with HIPAA requirements.',
  },
  {
    term: 'You are the author',
    desc: 'Every report comes back as a draft for you to review, edit, and sign.',
  },
]

function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [googleWidth, setGoogleWidth] = useState(G_WIDTH_NARROW)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const userLogin = useSelector((state) => state.userLogin)
  const { error, loading, userInfo } = userLogin

  useEffect(() => {
    if (userInfo) {
      navigate('/', { replace: true })
    }
  }, [userInfo, navigate])

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 30rem)')
    const apply = () => setGoogleWidth(mq.matches ? G_WIDTH_WIDE : G_WIDTH_NARROW)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const canSubmit = email.trim().length > 0 && password.length > 0 && !loading

  const submitHandler = (e) => {
    e.preventDefault()
    if (!canSubmit) return
    dispatch({ type: USER_LOGOUT })
    dispatch(login(email, password))
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      dispatch({ type: USER_LOGIN_REQUEST })

      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      }

      const { data } = await axios.post(
        process.env.REACT_APP_GOLANGAPI + '/api/users/google-login',
        { credential: credentialResponse.credential },
        config
      )

      dispatch({
        type: USER_LOGIN_SUCCESS,
        payload: data,
      })

      localStorage.setItem('userInfo', JSON.stringify(data))
    } catch (error) {
      console.error('Google login error:', error)
      dispatch({
        type: USER_LOGIN_FAIL,
        payload:
          error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message,
      })
    }
  }

  const handleGoogleError = (error) => {
    console.error('Google Login Error:', error)
    dispatch({
      type: USER_LOGIN_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    })
  }

  return (
    <>
      <SEO
        title="Login - PCRAssist - PCR Assistant"
        description="Sign in to your PCR Assistant account to access AI-powered PCR protocol generation and management tools."
        canonicalUrl="/login"
      />

      <div className="ls">
        <div className="ls__wrap ls__grid">
          {/* ---- the task half ------------------------------------- */}
          <div className="ls__col">
            <header className="ls__head">
              <h1 className="ls__title">Sign in.</h1>
              <p className="ls__lede">
                Write reports, manage your service templates, and ask questions against your
                protocol.
              </p>
            </header>

            {error && (
              <p className="ls__error" role="alert">
                <AlertCircle size={16} aria-hidden="true" />
                <span>{typeof error === 'string' ? error : 'We couldn’t sign you in.'}</span>
              </p>
            )}

            <form className="ls__form" onSubmit={submitHandler} noValidate>
              <div className="ls__field">
                <label className="ls__label" htmlFor="ls-email">
                  Email address
                </label>
                <div className="ls__control">
                  <input
                    id="ls-email"
                    className="ls__input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>
              </div>

              <div className="ls__field">
                <label className="ls__label" htmlFor="ls-password">
                  Password
                </label>
                <div className="ls__control">
                  <input
                    id="ls-password"
                    className="ls__input ls__input--pw"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="ls__pwtoggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <EyeOff size={18} aria-hidden="true" />
                    ) : (
                      <Eye size={18} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" className="ls__btn ls__btn--primary" disabled={!canSubmit}>
                {loading ? 'Signing in…' : 'Sign in'}
                {!loading && <ArrowRight size={16} aria-hidden="true" />}
              </button>
            </form>

            <div className="ls__alt">
              <div className="ls__alt-head">
                <span className="ls__alt-label">Or continue with</span>
                <hr className="ls__alt-rule" />
              </div>
              <div className="ls__gbtn">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="outline"
                  shape="rectangular"
                  type="standard"
                  width={googleWidth}
                />
              </div>
            </div>

            <div className="ls__meta">
              <span>
                New to PCRAssist?{' '}
                <Link className="ls__link" to="/register">
                  Create an account
                </Link>
              </span>
              <span>
                <Link className="ls__link" to="/resetpwd">
                  Forgot your password?
                </Link>
              </span>
            </div>
          </div>

          {/* ---- the context half — the one graphite surface -------- */}
          <aside className="ls__panel" aria-labelledby="ls-panel-title">
            <span className="ls__panel-label">Data handling</span>
            <h2 id="ls-panel-title">Patient data does not stay with us</h2>
            <ul className="ls__panel-list">
              {ASSURANCES.map((a) => (
                <li className="ls__panel-item" key={a.term}>
                  <b>{a.term}</b>
                  <span>{a.desc}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </>
  )
}

export default LoginScreen
